import { hasDataScope, scopeKey, type DataScope } from "../workflowData/models";
import type { BuilderWorkflow, WorkflowDraft } from "./models";

export function usesClientBrain(workflow: BuilderWorkflow | undefined): boolean {
    return Boolean(workflow?.reminder?.project_summary?.context_selectors.includes("client.brain") || workflow?.boxes.some(box => box.context_selectors.includes("client.brain")));
}

export function usesProjectSources(workflow: BuilderWorkflow | undefined): boolean {
    return Boolean(workflow?.reminder?.project_summary || workflow?.reminder?.note_monitor || workflow?.boxes.some(box => box.context_selectors.length > 0));
}

export function initialBuilderProjectId(draft: WorkflowDraft): string | null {
    const summary = draft.workflow.reminder?.project_summary ?? draft.workflow.reminder?.note_monitor;
    return summary ? summary.project_id : usesProjectSources(draft.workflow) ? draft.preview?.project_id ?? null : null;
}

/** Scheduled summaries have no document boxes; preview uses the existing result presentation. */
export function builderPreviewWorkflow(workflow: BuilderWorkflow): BuilderWorkflow {
    const summary = workflow.reminder?.project_summary;
    const monitor = workflow.reminder?.note_monitor;
    if (monitor) return {
        kind: "document", reminder: null, title: workflow.title, description: workflow.description,
        boxes: [{ id: "note_monitor", title: workflow.title, instructions: workflow.reminder?.message ?? "", context_selectors: [], output_type: "text", options: [] }]
    };
    if (!summary) return workflow;
    return {
        kind: "document", reminder: null, title: workflow.title, description: workflow.description,
        timezone: workflow.timezone, data_query: summary.data_query ?? workflow.data_query,
        data_scope: workflow.data_scope ?? summary.data_scope,
        boxes: [{ analytics: summary.analytics, id: "project_summary", title: workflow.title, instructions: summary.instructions, context_selectors: summary.context_selectors, output_type: "text", options: [] }]
    };
}

export function currentBuilderPreview(draft: WorkflowDraft | undefined, projectId: string | null, dataScope?: DataScope | null): WorkflowDraft["preview"] {
    const preview = draft?.preview;
    if (!draft || !preview || preview.workflow_revision !== draft.workflow_revision) return null;
    if (dataScope) {
        if (scopeKey(preview.data_scope) !== scopeKey(dataScope)) return null;
    } else if (preview.project_id !== projectId) return null;
    if (usesProjectSources(draft.workflow) && !hasDataScope(dataScope, projectId)) return null;
    return preview;
}

export function visibleBuilderMessages(draft: WorkflowDraft | undefined, projectId: string | null, dataScope?: DataScope | null): WorkflowDraft["messages"] {
    return draft?.messages.filter(message =>
        (dataScope ? (!message.data_scope && message.project_id === null) || scopeKey(message.data_scope) === scopeKey(dataScope) : message.project_id === null || message.project_id === projectId) &&
        (message.role !== "assistant" || message.intent !== "build")
    ) ?? [];
}

export function withoutClientResults(draft: WorkflowDraft | undefined, projectId: string | null): WorkflowDraft | undefined {
    if (!draft) return draft;
    if (!projectId) return { ...draft, preview: null, messages: draft.messages.filter(message => !message.data_scope && message.project_id === null) };
    return {
        ...draft,
        preview: draft.preview?.project_id === projectId ? null : draft.preview,
        messages: draft.messages.filter(message => message.project_id !== projectId)
    };
}
